import { useEffect } from "react";
import SEO_ROUTES from "../../data/seoRoutes.json";

const SITE_URL = "https://www.lunexalabs.com";
const DEFAULT_IMAGE = `${SITE_URL}/og.png`;

const setMeta = (selector, attributes) => {
	let meta = document.head.querySelector(selector);
	if (!meta) {
		meta = document.createElement("meta");
		document.head.appendChild(meta);
	}
	Object.entries(attributes).forEach(([key, value]) => meta.setAttribute(key, value));
};

const usePageMetadata = (title, description, options = {}) => {
	const rawPathname = options.pathname || (typeof window !== "undefined" ? window.location.pathname : "/");
	const pathname = rawPathname.replace(/\/+$/, "") || "/";
	const routeMetadata = SEO_ROUTES.find((item) => item.path === pathname)
		|| SEO_ROUTES.find((item) => item.path === "*")
		|| {};
	const resolvedTitle = options.title || routeMetadata.title || title || "Lunexa Labs";
	const resolvedDescription = options.description || routeMetadata.description || description
		|| "Independent software product studio creating mobile apps, web software, games, and digital tools.";
	const canonicalPath = options.canonicalPath || routeMetadata.canonicalPath || pathname;
	const canonicalUrl = `${SITE_URL}${canonicalPath === "/" ? "/" : canonicalPath}`;
	const image = options.image || routeMetadata.image || DEFAULT_IMAGE;
	const imageAlt = options.imageAlt || routeMetadata.imageAlt || "Lunexa Labs software product studio";
	const noIndex = options.noIndex ?? routeMetadata.noIndex ?? false;
	const robots = noIndex
		? "noindex, follow"
		: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
	const structuredData = options.structuredData || routeMetadata.structuredData;
	const keywords = options.keywords || routeMetadata.keywords;

	useEffect(() => {
		document.title = resolvedTitle;
		setMeta('meta[name="description"]', { name: "description", content: resolvedDescription });
		setMeta('meta[name="robots"]', { name: "robots", content: robots });
		setMeta('meta[name="googlebot"]', { name: "googlebot", content: robots });
		if (keywords) {
			setMeta('meta[name="keywords"]', { name: "keywords", content: keywords });
		}

		let canonical = document.head.querySelector('link[rel="canonical"]');
		if (!canonical) {
			canonical = document.createElement("link");
			canonical.setAttribute("rel", "canonical");
			document.head.appendChild(canonical);
		}
		canonical.setAttribute("href", canonicalUrl);

		setMeta('meta[property="og:title"]', { property: "og:title", content: resolvedTitle });
		setMeta('meta[property="og:description"]', { property: "og:description", content: resolvedDescription });
		setMeta('meta[property="og:type"]', { property: "og:type", content: options.type || routeMetadata.type || "website" });
		setMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
		setMeta('meta[property="og:image"]', { property: "og:image", content: image });
		setMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: imageAlt });
		setMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "Lunexa Labs" });
		setMeta('meta[property="og:locale"]', { property: "og:locale", content: "en_US" });
		setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
		setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: resolvedTitle });
		setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: resolvedDescription });
		setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });
		setMeta('meta[name="twitter:image:alt"]', { name: "twitter:image:alt", content: imageAlt });

		let schema = document.head.querySelector("#lunexa-page-structured-data");
		if (structuredData) {
			if (!schema) {
				schema = document.createElement("script");
				schema.id = "lunexa-page-structured-data";
				schema.type = "application/ld+json";
				document.head.appendChild(schema);
			}
			schema.textContent = JSON.stringify(structuredData);
		} else if (schema) {
			schema.remove();
		}
	}, [canonicalUrl, image, imageAlt, keywords, noIndex, options.type, resolvedDescription, resolvedTitle, robots, routeMetadata.type, structuredData]);
};

export default usePageMetadata;
